import type { PodcastConfig } from "./types";

export const PODCAST_CONFIG: PodcastConfig = {
  name: "Socrattica: El Podcast",
  tagline: "Preguntas que te llevan a dominar el inglés.",
  secondaryTagline: "El método antes que la suerte.",
  description:
    'SOCRATTICA es el podcast para quienes están cansados de "estudiar" inglés sin resultados. Cada episodio responde una pregunta clave sobre cómo aprender inglés de verdad: cómo memoriza el cerebro, por qué no puedes hablar aunque entiendes todo, cómo perder el miedo, y cómo construir un método que sí funcione. Con Roberto, magíster en la enseñanza del inglés, y un enfoque práctico basado en evidencia — no en mitos de internet.',
  shortDescription:
    "El podcast que te enseña a aprender inglés, no solo a estudiarlo. Método, mentalidad y práctica real. 🎙️🇬🇧",
  audience:
    "Hispanohablantes de nivel A1–C1 que quieren entender cómo aprender inglés, no solo memorizar reglas",
  format: "Monólogo educativo con explicaciones prácticas y referencias basadas en evidencia",
  language: "Español",
  episodeDuration: "15–22 minutos",
  seasonOneTagline: "Siete episodios. Un método. Cero mitos.",
  author: "Roberto Román Lázaro",
  coverImage: "/socrattica.jpg",
  rssUrl: "https://anchor.fm/s/1154b3c08/podcast/rss",
  platforms: [
    {
      name: "Apple Podcasts",
      url: "https://podcasts.apple.com/us/podcast/socrattica-el-podcast/id6794500236",
      kind: "apple",
    },
    {
      name: "Spotify",
      url: "https://open.spotify.com/show/033Vj1gTSHXI1dz2zIZlhx?si=unJe-OIHQJKvO0Ydo7IgCg",
      kind: "spotify",
    },
    {
      name: "YouTube",
      url: "https://www.youtube.com/playlist?list=PLNDpF0fbDWhk",
      kind: "youtube",
    },
  ],
  episodes: [
    {
      id: "d6085f10-882e-4d18-a80e-075bca8764bb",
      slug: "como-aprende-tu-cerebro-un-idioma-y-por-que-la-gramatica-sola-no-funciona",
      title: "Cómo aprende tu cerebro un idioma, y por qué la gramática sola no funciona",
      description:
        "Tu cerebro no aprende un idioma solo memorizando reglas. Descubre cómo funciona el input comprensible, qué papel cumple realmente la gramática y cómo transformar tus hábitos diarios en oportunidades reales para adquirir inglés.",
      publishedAt: "2026-08-06T19:00:00Z",
      duration: "7:23",
      type: "Episodio 02",
      season: 1,
      episode: 2,
      episodeType: "full",
      url: "https://podcasters.spotify.com/pod/show/socrattica/episodes/Cmo-aprende-tu-cerebro-un-idioma--y-por-qu-la-gramtica-sola-no-funciona-e3n47u6",
      keyTakeaways: [
        "El input comprensible permite asociar formas del idioma con significados dentro de un contexto.",
        "La gramática ayuda a notar patrones, pero conocer una regla no basta para usarla con fluidez.",
        "Los hábitos cotidianos funcionan mejor cuando combinan comprensión, atención y uso frecuente del idioma.",
      ],
      practice: {
        title: "Convierte una regla en input comprensible",
        duration: "7 minutos",
        steps: [
          "Elige una estructura gramatical que hayas estudiado recientemente.",
          "Busca un audio o texto breve donde esa estructura aparezca en un contexto que puedas entender.",
          "Anota tres ejemplos y explica qué significa cada uno sin recitar la regla.",
        ],
        reflectionPrompt:
          "¿Tu rutina te permite comprender y reconocer el inglés en contexto, o se concentra solamente en recordar explicaciones?",
      },
      references: [
        {
          title: "Measuring implicit and explicit knowledge of a second language",
          authors: "Rod Ellis (2005)",
          description:
            "Estudio psicométrico que distingue el conocimiento explícito de reglas del conocimiento implícito disponible durante el uso del idioma.",
          url: "https://doi.org/10.1017/S0272263105050096",
        },
        {
          title: "Interactions between type of instruction and type of language feature",
          authors: "Nina Spada y Yasuyo Tomita (2010)",
          description:
            "Metaanálisis sobre los efectos de distintos tipos de instrucción en el aprendizaje de características lingüísticas.",
          url: "https://doi.org/10.1111/j.1467-9922.2010.00562.x",
        },
        {
          title: "Marco Común Europeo de Referencia — volumen complementario",
          authors: "Consejo de Europa (2020)",
          description:
            "Marco orientado al uso del idioma mediante actividades comunicativas y objetivos observables.",
          url: "https://www.coe.int/en/web/common-european-framework-reference-languages/cefr-companion-volume-and-its-language-versions",
        },
      ],
    },
    {
      id: "479eb4cb-f29e-481e-9694-9f143661d0f7",
      slug: "por-que-no-aprendes-ingles-en-realidad-aunque-llevas-anos-intentandolo",
      title: "¿Por qué no aprendes Inglés en realidad aunque llevas años intentándolo?",
      description:
        "Estudiar inglés no siempre significa adquirirlo. Este episodio explica la diferencia entre memorizar reglas y desarrollar habilidades reales, los mitos que frenan el progreso y por qué la motivación necesita un sistema.",
      publishedAt: "2026-07-30T05:00:00Z",
      duration: "7:15",
      type: "Episodio 01",
      season: 1,
      episode: 1,
      episodeType: "full",
      url: "https://podcasters.spotify.com/pod/show/socrattica/episodes/Por-qu-no-aprendes-Ingls-en-realidad-aunque-llevas-aos-intentndolo-e3mlto4",
      keyTakeaways: [
        "Saber explicar una regla no garantiza poder usarla mientras hablas, escuchas o escribes bajo presión.",
        "El progreso debe medirse con actuaciones reales: comprender, recuperar palabras y producir mensajes sin depender siempre de la traducción.",
        "La motivación ayuda a comenzar, pero un sistema con práctica concreta, frecuencia y retroalimentación sostiene el avance.",
      ],
      practice: {
        title: "Audita cómo estás aprendiendo",
        duration: "7 minutos",
        steps: [
          "Elige una habilidad que dices estar estudiando: escuchar, hablar, leer o escribir.",
          "Anota qué hiciste realmente durante tus últimas tres sesiones y clasifica cada actividad como explicación o práctica.",
          "Sustituye una actividad pasiva por una tarea observable que reproduzca el uso real del inglés.",
        ],
        reflectionPrompt:
          "¿Tu rutina actual entrena la habilidad que quieres desarrollar o solamente te ayuda a sentir que estás estudiando?",
      },
      references: [
        {
          title: "Measuring implicit and explicit knowledge of a second language",
          authors: "Rod Ellis (2005)",
          description:
            "Estudio psicométrico que distingue el conocimiento explícito de reglas del conocimiento implícito disponible durante el uso del idioma.",
          url: "https://doi.org/10.1017/S0272263105050096",
        },
        {
          title: "Interactions between type of instruction and type of language feature",
          authors: "Nina Spada y Yasuyo Tomita (2010)",
          description:
            "Metaanálisis sobre los efectos de distintos tipos de instrucción en el aprendizaje de características lingüísticas.",
          url: "https://doi.org/10.1111/j.1467-9922.2010.00562.x",
        },
        {
          title: "Marco Común Europeo de Referencia — volumen complementario",
          authors: "Consejo de Europa (2020)",
          description:
            "Marco orientado a lo que una persona puede hacer con el idioma mediante actividades y objetivos comunicativos observables.",
          url: "https://www.coe.int/en/web/common-european-framework-reference-languages/cefr-companion-volume-and-its-language-versions",
        },
      ],
      relatedResources: [
        {
          title: "Plan de Estudio Intensivo C1 — 30 días",
          description:
            "Transforma objetivos generales en tareas diarias medibles para las cinco habilidades.",
          href: "/productos/plan-estudio-intensivo-c1-30-dias",
        },
        {
          title: "Vocabulario Avanzado C1",
          description: "Entrena recuperación, colocaciones y uso contextual de vocabulario.",
          href: "/productos/vocabulario-avanzado-c1",
        },
        {
          title: "Guía Completa de Writing C1",
          description: "Pasa de leer explicaciones a planificar, producir y evaluar textos reales.",
          href: "/productos/guia-completa-writing-c1",
        },
      ],
    },
    {
      id: "196f848e-97bd-405c-a89b-ca3805b4f999",
      slug: "trailer-socrattica-el-podcast",
      title: "Socrattica: El Podcast — Tráiler",
      description:
        "¿Cuántos años llevas intentando aprender inglés y sientes que sigues en el mismo lugar? Descubre por qué no es falta de disciplina y cómo aprende tu cerebro un idioma de verdad.",
      publishedAt: "2026-07-25T00:32:20Z",
      duration: "1:02",
      type: "Tráiler",
      url: "https://podcasters.spotify.com/pod/show/socrattica/episodes/Socrattica-El-Podcast--Triler-e3mh23a",
      transcript: [
        "¿Cuántos años llevas intentando aprender inglés y sientes que sigues en el mismo lugar? No es falta de disciplina: es que nadie te ha explicado cómo aprende tu cerebro un idioma de verdad.",
        "Bienvenido a Socrattica: El Podcast. Cada semana, un episodio corto y directo sobre cómo aprender inglés de verdad: por qué no has avanzado todavía, cómo funciona la adquisición de un idioma, el vocabulario que sí se queda y la gramática que se internaliza, no solo se memoriza.",
        "También hablaremos de cómo hablar sin miedo, cómo entrenar tu oído y cómo construir tu propio plan de 30 días.",
        "Siete episodios. Un método. Cero mitos.",
        "Sígueme en Instagram para más contenido entre episodios y suscríbete para no perderte el primer capítulo, muy pronto.",
      ],
      keyTakeaways: [
        "Sentirse estancado no demuestra falta de disciplina: muchas veces revela que la práctica no coincide con la habilidad que se quiere desarrollar.",
        "Aprender un idioma exige recuperar, usar y revisar lo estudiado; acumular explicaciones no basta.",
        "Un sistema pequeño y repetible puede ser más útil que depender de picos de motivación.",
      ],
      practice: {
        title: "Convierte una intención en práctica",
        duration: "5 minutos",
        steps: [
          "Escribe una situación concreta en la que hoy necesitas usar inglés.",
          "Define una práctica de diez minutos que reproduzca esa situación.",
          "Elige una evidencia observable para comprobar dentro de siete días si mejoraste.",
        ],
        reflectionPrompt:
          "¿Qué cambiaría esta semana si dejaras de medir el progreso por horas estudiadas y lo midieras por lo que ya puedes hacer?",
      },
      references: [
        {
          title: "Retrieval practice is better than imitation for foreign vocabulary learning",
          authors: "Kang, Gollan y Pashler (2013)",
          description:
            "Estudio experimental sobre recuperación activa, comprensión, producción y pronunciación de vocabulario en una segunda lengua.",
          url: "https://pubmed.ncbi.nlm.nih.gov/23681928/",
        },
        {
          title: "The effects of distributed practice on second language fluency development",
          authors: "Kakitani y Kormos (2024)",
          description:
            "Investigación con práctica espaciada y mediciones posteriores del desarrollo de fluidez oral en inglés como segunda lengua.",
          url: "https://www.cambridge.org/core/journals/studies-in-second-language-acquisition/article/effects-of-distributed-practice-on-second-language-fluency-development/4F6787916C198376CAD222934D3B37E4",
        },
        {
          title: "Marco Común Europeo de Referencia — volumen complementario",
          authors: "Consejo de Europa (2020)",
          description:
            "Marco oficial para orientar aprendizaje, enseñanza, evaluación y objetivos comunicativos por nivel.",
          url: "https://www.coe.int/en/web/common-european-framework-reference-languages/cefr-companion-volume-and-its-language-versions",
        },
      ],
      relatedResources: [
        {
          title: "Plan de Estudio Intensivo C1 — 30 días",
          description:
            "Convierte el aprendizaje en tareas diarias medibles para las cinco habilidades.",
          href: "/productos/plan-estudio-intensivo-c1-30-dias",
        },
        {
          title: "Vocabulario Avanzado C1",
          description: "Practica familias de palabras, colocaciones y recuperación activa.",
          href: "/productos/vocabulario-avanzado-c1",
        },
        {
          title: "Guía Completa de Writing C1",
          description: "Modelos anotados, planificación, práctica y autoevaluación de escritura.",
          href: "/productos/guia-completa-writing-c1",
        },
      ],
    },
  ],
};
